import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../utils/Enums';
import { apiClient } from '../../utils/ApiClient';
import { AdvertisingSetting, UpdateAdvertisingSetting } from '../../models/AdvertisingSetting';

interface AdvertisingSettingsState {
    settings: AdvertisingSetting[];
    stats: {
        total_contacts: number;
        sent_count: number;
        current_position: string;
        current_cycle: number;
    } | null;
    getSettingsStatus: RequestStatus;
    updateSettingsStatus: RequestStatus;
    errors: string[];
}

const initialState: AdvertisingSettingsState = {
    settings: [],
    stats: null,
    getSettingsStatus: RequestStatus.Idle,
    updateSettingsStatus: RequestStatus.Idle,
    errors: []
};

export const getSettings = createAsyncThunk('advertisingSettings/getSettings', async (_, { rejectWithValue }) => {
    try {
        const response = await apiClient.get('advertising-settings');
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Failed to fetch advertising settings');
    }
});

export const updateSettings = createAsyncThunk('advertisingSettings/updateSettings', async (updatedSettings: UpdateAdvertisingSetting[], { rejectWithValue }) => {
    try {
        const response = await apiClient.put('advertising-settings', updatedSettings);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Failed to update advertising settings');
    }
});

const advertisingSettingsSlice = createSlice({
    name: 'advertisingSettings',
    initialState,
    reducers: {
        resetUpdateSettingsStatus: (state) => {
            state.updateSettingsStatus = RequestStatus.Idle;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getSettings.pending, (state) => {
                state.getSettingsStatus = RequestStatus.Loading;
            })
            .addCase(getSettings.fulfilled, (state, action) => {
                state.getSettingsStatus = RequestStatus.Success;
                state.settings = action.payload.settings;
                state.stats = action.payload.stats;
            })
            .addCase(getSettings.rejected, (state) => {
                state.getSettingsStatus = RequestStatus.Failed;
            })
            .addCase(updateSettings.pending, (state) => {
                state.updateSettingsStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateSettings.fulfilled, (state) => {
                state.updateSettingsStatus = RequestStatus.Success;
            })
            .addCase(updateSettings.rejected, (state, action) => {
                state.updateSettingsStatus = RequestStatus.Failed;
                const errors = action.payload;
                if (errors && typeof errors === 'object' && 'errors' in errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            });
    },
});

export const { resetUpdateSettingsStatus } = advertisingSettingsSlice.actions;
export default advertisingSettingsSlice.reducer;